/**
 * Payout timesheet dashboard table settings.
 * Tweak widths, labels, classes, and copy here without digging through render code.
 */

/** Status pill labels/styles used in the Status column. */
export const PAYOUT_TIMESHEET_STATUS = {
  pending: {
    label: "Pending",
    className: "border-gray-200 bg-gray-100 text-gray-700",
  },
  approving: {
    label: "Approving",
    className: "border-yellow-200 bg-yellow-50 text-yellow-800",
  },
  approved: {
    label: "Approved",
    className: "border-green-200 bg-green-50 text-green-800",
  },
  exported: {
    label: "Exported",
    className: "border-green-900 bg-green-900 text-white",
  },
  edited: {
    label: "Edited",
    className: "border-orange-200 bg-yellow-200 text-orange-900",
  },
  noHours: {
    label: "No Hrs",
    className: "border-gray-200 bg-gray-100 text-gray-700",
  },
};

/** Native tooltip / helper copy used by headers and cells. */
export const PAYOUT_TIMESHEET_TOOLTIPS = {
  hoursLogWarning: "Some manager hours logs are missing or incomplete",
  hoursLogComplete: "All shifts have manager hours logs completed",
  managerHoursMissing: "Manager hours logs missing",
  managerDeltaHeader: "Difference between worker hours and manager hours.",
  payoutDeltaHeader: "Difference between worker hours and payout hours.",
  primaryProjectHeader:
    "Most common project for this worker this week. Hover for full breakdown.",
  payoutHoursMissing: "Payout hours missing",
  noHoursStatus:
    "QuickBooks was exported and this worker has no shifts for the week.",
};

/**
 * Column layout settings (order = display order).
 * `width` values should sum to ~100% for a snug `table-fixed` fit.
 */
export const PAYOUT_TIMESHEET_COLUMNS = [
  {
    key: "workerName",
    label: "Worker",
    width: "18%",
    align: "left",
    sortable: true,
    headerClassName: "px-2 py-2text-left font-semibold",
    className: "truncate whitespace-nowrap px-2 py-1 pl-3 text-gray-950",
  },
  {
    key: "shiftCount",
    label: "Shifts",
    width: "7%",
    align: "right",
    sortable: true,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums",
  },
  {
    key: "primaryProject",
    label: "Main Project",
    width: "11%",
    align: "right",
    sortable: true,
    headerTitle: PAYOUT_TIMESHEET_TOOLTIPS.primaryProjectHeader,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right",
  },
  {
    key: "workerHours",
    label: "Worker Hrs",
    width: "10%",
    align: "right",
    sortable: true,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums",
  },
  {
    key: "hoursLogHours",
    label: "Manager Hrs",
    width: "10%",
    align: "right",
    sortable: true,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums",
  },
  {
    key: "payoutHours",
    label: "Payout Hrs",
    width: "10%",
    align: "right",
    sortable: true,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums text-gray-950",
  },
  {
    key: "managerDelta",
    label: "Manager Delta",
    width: "11%",
    align: "right",
    sortable: true,
    headerTitle: PAYOUT_TIMESHEET_TOOLTIPS.managerDeltaHeader,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums",
  },
  {
    key: "payoutDelta",
    label: "Payout Delta",
    width: "11%",
    align: "right",
    sortable: true,
    headerTitle: PAYOUT_TIMESHEET_TOOLTIPS.payoutDeltaHeader,
    headerClassName: "px-1.5 py-2 text-right font-semibold",
    className: "px-1.5 py-1 text-right tabular-nums",
  },
  {
    key: "status",
    label: "Status",
    width: "12%",
    align: "right",
    sortable: true,
    headerClassName: "py-2 pl-3 pr-3 text-right font-semibold",
    className: "py-1 pl-3 pr-3 text-right",
  },
];

/** SimpleTable chrome / behavior for the compact payout table. */
export const PAYOUT_TIMESHEET_TABLE = {
  includeSearch: false,
  // Default: Main Project descending (project number).
  defaultSort: { key: "primaryProject", direction: "desc" },
  emptyMessage: "No timesheets found.",
  weekEmptyMessage: "No payout timesheets found for this week.",
  className: "w-full min-w-0",
  tableClassName: "w-full table-fixed divide-y divide-gray-100",
  theadClassName: "bg-gray-100",
  headerRowClassName:
    "whitespace-nowrap text-[11px] uppercase tracking-wide text-gray-500",
  tbodyClassName: "divide-y divide-gray-100 bg-white",
  cellClassName: "px-1.5 py-1",
  rowClassName: {
    actionable:
      "group cursor-pointer text-xs text-gray-700 transition hover:bg-primary-50/60",
    muted: "group cursor-default bg-gray-50/40 text-xs text-gray-600",
  },
  statusPillClassName:
    "inline-flex min-w-16 text-center justify-center rounded-full border px-2 py-0.5 text-[11px]",
  hoursValueClassName: {
    warning: "text-yellow-800",
    complete: "text-green-900",
  },
};

/** Week card wrapper around the compact table. */
export const PAYOUT_TIMESHEET_WEEK_SECTION = {
  wrapperClassName: "bg-gray-100 shadow-md rounded-md shadow-gray-300",
  sectionClassName:
    "w-full min-w-0 pb-2 overflow-hidden rounded-lg border-white/70 bg-white shadow-lg shadow-primary-900/10",
};
